/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/
import classnames from 'classnames';
import { computed, observable, reaction, set } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';

import { IModuleStateProps, withModuleState } from '@msdyn365-commerce-modules/checkout-utilities';
import { IModuleProps } from '@msdyn365-commerce-modules/utilities';
import {
    Address, AttributeValue, CountryRegionInfo, GiftCard, StateProvinceInfo
} from '@msdyn365-commerce/retail-proxy';

import { AddressCommon } from '../../common/address-common';
import { AddressFormat } from '../../common/address-format';
import { AddressItemType } from '../../common/address-format.data';
import { AddressMetaData } from '../../common/address-meta-data';
import {
    AddressOperation, AddressType, IAddressResource, IAddressResponse
} from '../../common/address-module.data';
import { AddressAddUpdate, IAddressAddUpdateProps } from '../../common/components/address-add';
import AddressBillingCheckoxComponent from '../../common/components/address-billing-checkbox';
import AddressBillingHeadingComponent from '../../common/components/address-billing-heading';
import { AddressShow, IAddressShowProps } from '../../common/components/address-show';

import { ICheckoutState } from '@msdyn365-commerce/global-state';
import { WineClubDataStore } from '../wine-club-signup/wine-club-data';
import { ISmweCheckoutBillingAddressData } from './smwe-checkout-billing-address.data';
import { ISmweCheckoutBillingAddressProps } from './smwe-checkout-billing-address.props.autogenerated';
import { updateAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/CartsDataActions.g';

export interface IProps extends ISmweCheckoutBillingAddressProps<ISmweCheckoutBillingAddressData>, IModuleStateProps { }

export interface ICheckoutBillingAddressViewState {
    isShowAddress: boolean;
    isShowAddOrUpdateAddress: boolean;
    isShowSameAsShippingCheckbox: boolean;
}

export interface ICheckoutBillingAddressViewProps extends IProps {
    className: string;
    currentOperation: AddressOperation;
    selectedAddress?: Address;
    addUpdateAddress: Address;
    countryRegionId: string;
    stateProvinceInfo?: StateProvinceInfo[];
    validationError: object;
    addressActionResponse?: IAddressResponse;
    viewState: ICheckoutBillingAddressViewState;
    CheckoutBillingAddress: IModuleProps;
    sameAsShippingCheckbox: React.ReactNode;
    heading: React.ReactNode;
    showAddress: IAddressShowProps;
    showAddOrUpdateAddress: IAddressAddUpdateProps;
}

/**
 *
 * CheckoutBillingAddress component
 * @extends {React.PureComponent<ISmweCheckoutBillingAddressProps<ISmweCheckoutBillingAddressData>>}
 */
// @ts-ignore
@withModuleState
@observer
class CheckoutBillingAddress extends React.Component<IProps> {

    @observable private currentOperation: AddressOperation;
    @observable private isBillingAddressSameAsShipping: boolean;
    @observable private selectedAddress?: Address;
    @observable private addUpdateAddress: Address;
    @observable private countryRegionId: string = 'USA';
    @observable private stateProvinceInfo?: StateProvinceInfo[];
    @observable private validationError: object;
    @observable private addressActionResponse?: IAddressResponse;

    private addressCommon: AddressCommon;
    private addressFormat: AddressFormat;
    private countryRegions: CountryRegionInfo[];
    private customerAccountNumber?: string;
    private resources: IAddressResource;
    // private addRenderRef: React.RefObject<AddressAdd>;

    constructor(props: IProps) {
        super(props);
        const { context, data, resources, telemetry } = this.props;

        this.addUpdateAddress = {};
        this.validationError = {};
        this.isBillingAddressSameAsShipping = false;
        this.resources = resources;
        this.currentOperation = AddressOperation.List;
        this.customerAccountNumber = context && context.request && context.request.user && context.request.user.customerAccountNumber;
        this.countryRegions = data.countryRegions.result || [];
        this.stateProvinceInfo = data.countryStates.result || [];
        this.addressFormat = new AddressFormat(context, this.countryRegions, new AddressMetaData({ ...resources }));
        this.addressCommon = new AddressCommon(context, resources, telemetry, this.customerAccountNumber);

        // this.addRenderRef = React.createRef();
    }

    public componentDidMount(): void {
        this.props.data.checkout.then(() => {
            this._initModuleState();
            this._setDefaultCountryRegionId();
        }).catch(e => this.props.telemetry.exception(e));

        reaction(
            () => this.countryRegionId,
            () => this._getStateProvinces()
        );

        reaction(
            () => this.currentOperation,
            () => {
                this._getStateProvinces();
            }
        );

        reaction(
            () => this.props.data.checkout.result && this.props.data.checkout.result.shippingAddress,
            shippingAddress => {
                if (this.props.moduleState.isUpdating) {
                    if (shippingAddress && this.isBillingAddressSameAsShipping) {
                        this.isBillingAddressSameAsShipping = true;
                        this._updateCurrentOperation(AddressOperation.Show, shippingAddress);
                    } else if (shippingAddress && this.isBillingAddressSameAsShipping) {
                        this._updateCurrentOperation(AddressOperation.Show, shippingAddress);
                    }
                }
            }
        );

        reaction(
            () => this.isBillingAddressRequried,
            isBillingAddressRequried => {
                if (isBillingAddressRequried && this.props.moduleState.isDisabled) {
                    this.onEdit();
                } else {
                    this.props.moduleState.onDisable();
                }
            }
        );
    }

    public render(): JSX.Element | null {
        if (!(this.isBillingAddressRequried)) {
            return null;
        }

        const { resources } = this.props;

        const { renderView, config } = this.props;

        const clubStore = WineClubDataStore.getInstance().get();
        const wineState = this.props.moduleState.getModuleByTypeName('checkout-club-info-collector').hasModuleState;

        let isShowSameAsShippingCheckbox = this.isCartContainsItemsForShipping && this.props.moduleState.isUpdating;
        if (clubStore.deliveryType === 'PICKUP' && wineState) {
            isShowSameAsShippingCheckbox = false;
        }

        const viewProps = {
            ...this.props,
            currentOperation: this.currentOperation,
            selectedAddress: this.selectedAddress,
            addUpdateAddress: this.addUpdateAddress,
            countryRegionId: this.countryRegionId,
            stateProvinceInfo: this.stateProvinceInfo,
            validationError: this.validationError,
            addressActionResponse: this.addressActionResponse,
            className: config.className,
            viewState: {
                isShowAddress: this.selectedAddress && this.currentOperation === AddressOperation.Show,
                isShowAddOrUpdateAddress: this.currentOperation !== AddressOperation.Show,
                isShowSameAsShippingCheckbox: isShowSameAsShippingCheckbox
            },
            CheckoutBillingAddress: {
                moduleProps: this.props,
                className: classnames('ms-checkout-billing-address', config.className)
            },
            heading: <AddressBillingHeadingComponent {...{ text: resources.addressBillingAddressHeading }} />,

            // tslint:disable-next-line: max-line-length
            sameAsShippingCheckbox: (
                <AddressBillingCheckoxComponent
                    {
                    ...{
                        isChecked: this.isBillingAddressSameAsShipping,
                        type: 'checkbox',
                        value: 'true',
                        ariaLabel: resources.addressSameAsShippingAddressAriaLabel,
                        text: resources.addressSameAsShippingAddressText,
                        onChange:
                            this._onBillingAddressSameAsShippingChange,
                        wineState: this.props.moduleState.getModuleByTypeName('checkout-club-info-collector').hasModuleState
                    }
                    }
                />
            ),
            showAddress: this._renderShowAddress(),
            showAddOrUpdateAddress: this._renderAddOrUpdateAddress()
        };

        return renderView(viewProps) as React.ReactElement;
    }

    private _renderAddOrUpdateAddress = (): IAddressAddUpdateProps => {
        return AddressAddUpdate(
            {
                resources: this.resources,
                addressType: AddressType.Billing,
                addressFormat: this.addressFormat.getAddressFormat((this.addUpdateAddress.ThreeLetterISORegionName) || this.countryRegionId),
                defaultCountryRegionId: this.countryRegionId,
                selectedAddress: this.addUpdateAddress,
                validationError: this.validationError,
                addressActionResponse: this.addressActionResponse,
                dropdownDisplayData: this.addressFormat.getPrefilledAddressDropdownData(this.resources.addressStateDefaultSelectionText, this.stateProvinceInfo),
                onInputChange: this._onAddressAddUpdateInputChange,
                onDropdownChange: this._onAddressAddUpdateDropdownChange,
                hasExternalSubmitGroup: this.props.moduleState.hasExternalSubmitGroup,
                onSave: this._onSubmitAddress,
                onCancel: this._resetView
            }
        );
    };

    private _renderShowAddress(): IAddressShowProps | null {
        if (this.selectedAddress) {
            return AddressShow({ address: this.selectedAddress, addressFormat: this.addressFormat.getAddressFormat(this.selectedAddress.ThreeLetterISORegionName || '') });
        }

        return null;
    }

    private _onCountryChange = (countryRegionId: string) => {
        this.countryRegionId = countryRegionId;
        set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: countryRegionId });
    };

    private onAddressAddUpdate = (name: string, value: string) => {
        set(this.addUpdateAddress, { [name]: value });
    };

    private _onAddressAddUpdateInputChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
        const value = (event.target.value || '').replace(new RegExp('[<>]', 'gi'), '');
        this.onAddressAddUpdate(event.target.name, value);
    };

    private _onAddressAddUpdateDropdownChange = (event: React.ChangeEvent<HTMLSelectElement>): void => {
        this.onAddressAddUpdate(event.target.name, event.target.value);

        if (event.target.name === AddressItemType[AddressItemType.ThreeLetterISORegionName]) {
            this._onCountryChange(event.target.value);
        }
    };

    private _getStateProvinces = (): void => {
        if (!this.countryRegionId || !(this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update)) {
            return;
        }

        // tslint:disable-next-line:no-floating-promises
        this.addressCommon.getStateProvinces(this.countryRegionId).then(
            (result: StateProvinceInfo[]) => {
                this.stateProvinceInfo = result;
            }).catch(e => this.props.telemetry.exception(e));
    };

    private _initModuleState = (): void => {
        this.props.moduleState.init({
            status: this.isBillingAddressRequried ? 'updating' : 'disabled',
            onEdit: this.onEdit,
            onCancel: this.onCancel,
            onSubmit: this.onSubmit
        });

        if (this.isBillingAddressRequried) {
            const { checkout } = this.props.data;
            const cart = checkout.result && checkout.result.checkoutCart.cart;
            if (this.isCartContainsItemsForShipping) {
                const clubStore = WineClubDataStore.getInstance().get();
                const wineState = this.props.moduleState.getModuleByTypeName('checkout-club-info-collector').hasModuleState;
                if (clubStore.deliveryType === 'PICKUP' && wineState) {
                    this.isBillingAddressSameAsShipping = false;
                    this._updateCurrentOperation(AddressOperation.Add);
                } else {
                    this.isBillingAddressSameAsShipping = true;
                    this._updateCurrentOperation(AddressOperation.Show, this.shippingAddress);
                }
            } else if (cart && cart.CartLines && cart.CartLines.filter(item=>item.ItemId==='GIFTCARD').length===1) {
                if (this.billingAddress) {
                    this._updateCurrentOperation(AddressOperation.Show, this.billingAddress);
                } else {
                    this._updateCurrentOperation(AddressOperation.Add);
                }
            } else {
                this.isBillingAddressSameAsShipping = false;
                this._updateCurrentOperation(AddressOperation.Add);
            }
        }
    };

    private _setDefaultCountryRegionId(): void {
        const { request } = this.props.context;
        const market = request && request.channel && request.channel.ChannelCountryRegionISOCode;

        this.countryRegionId = this.addressCommon.getDefaultCountryRegionId(this.countryRegionId, this.countryRegions, market);
    }

    private _onBillingAddressSameAsShippingChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        this.isBillingAddressSameAsShipping = event.currentTarget.checked;

        if (this.isBillingAddressSameAsShipping && this.shippingAddress) {
            this._updateCurrentOperation(AddressOperation.Show, this.shippingAddress);
        } else {
            this._updateCurrentOperation(AddressOperation.Add, this.billingAddress);
        }
    };

    @computed get isCartContainsItemsForShipping(): boolean {
        const { request } = this.props.context;

        const pickupDeliveryModeCode = request && request.channel && request.channel.PickupDeliveryModeCode;
        return ((this.props.data.checkout.result && this.props.data.checkout.result.checkoutCart.cart.CartLines) || []).some(cartLine => cartLine.DeliveryMode !== pickupDeliveryModeCode);
    }

    @computed get billingAddress(): Address | undefined {
        return this.props.data.checkout.result && this.props.data.checkout.result.billingAddress;
    }

    @computed get shippingAddress(): Address | undefined {
        return this.props.data.checkout.result && this.props.data.checkout.result.shippingAddress;
    }

    @computed get getLoyaltyAmount(): number {
        const checkoutState = this.props.data.checkout.result;
        return checkoutState && checkoutState.loyaltyAmount ? checkoutState.loyaltyAmount : 0;
    }

    @computed get getGiftCardTotalAmount(): number {
        const checkoutState = this.props.data.checkout.result;
        if (!checkoutState || !checkoutState.giftCards) {
            return 0;
        }
        return checkoutState.giftCards.reduce(
            (count: number, giftCard: GiftCard) => {
                return count + (giftCard.Balance || 0);
            },
            0);
    }

    @computed get shouldPaidByCard(): boolean {
        const cart = this.props.data.checkout.result ? this.props.data.checkout.result.checkoutCart.cart : undefined;
        if (!cart || !cart.CartLines || !cart.CartLines.length) {
            return false;
        }
        // Use payment card when it is not free or gift card balance + Loyalty amount can not cover the total amount
        const amountDue = (cart.TotalAmount || 0) - this.getGiftCardTotalAmount - this.getLoyaltyAmount;
        return amountDue > 0;
    }

    @computed get isBillingAddressRequried(): boolean {
        return this.shouldPaidByCard;
    }

    private _onSubmitAddress = (): void => {
        const isBilling = true;
        if (!this.addressFormat.validateAddressFormat(this.addUpdateAddress, this.validationError, this.countryRegionId, isBilling)) {
            this.props.moduleState.setHasError(true);
            return;
        }

        void this._setBillingAddress(this.addUpdateAddress).then(async () => {
            this._updateCurrentOperation(AddressOperation.Show, this.addUpdateAddress);
            this._updateModuleState();
            // tslint:disable-next-line:no-floating-promises
            await this.updateCartDeliverySpecification();
        });
    };

    private updateCartDeliverySpecification = async(): Promise<void> => {
        const { resources } = this.props;
        const checkoutState = this.props.data.checkout.result as ICheckoutState;
        if(this.props.data.checkout.result?.checkoutCart.cart &&
            this.props.data.checkout.result?.checkoutCart.cart.CartLines &&
            this.props.data.checkout.result?.checkoutCart.cart.CartLines.length > 0 &&
            this.props.data.checkout.result?.checkoutCart.cart.CartLines[0].DeliveryMode) {
                const updateCartDeliverySpecificationInput = {
                    deliveryModeId: this.props.data.checkout.result?.checkoutCart.cart.CartLines[0].DeliveryMode || '',
                    shippingAddress: checkoutState.shippingAddress
                };
                await checkoutState.checkoutCart.updateCartDeliverySpecification(updateCartDeliverySpecificationInput)
                    .then((response) => {
                        if (
                            response.status === 'FAILED' &&
                            response.substatus === 'NOCONTENT' &&
                            this._containsOnlyGifts()
                        ) {
                            this.onReady();
                            return;
                        }
                        if (response.status === 'FAILED') {
                            this.setError(resources.addressGenericErrorMessage);
                        } else {
                            this.onReady();
                        }
                    })
                    .catch(
                        () => {
                            this.setError(resources.addressGenericErrorMessage);
                        }
                    );
        }
    };

    private async _containsOnlyGifts(): Promise<boolean> {
        const cart = await this.props.data.checkout;
        const emailCode = this.props.context.request.channel?.EmailDeliveryModeCode;
        const nonDigitalItems = cart.checkoutCart.cart.CartLines?.filter(line => line.DeliveryMode !== emailCode);
        return nonDigitalItems?.length === 0;
    }

    private onReady = () => {
        this.props.moduleState.onReady();
    };

    private setError = (errorMessage: string): void => {
        this.props.telemetry.error(errorMessage);
        this.props.moduleState.setHasError(true);
        this.props.moduleState.onUpdating();
    };

    private onSubmit = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this._onSubmitAddress();
                break;
            case AddressOperation.Show:
                if (this.selectedAddress) {
                    void this._setBillingAddress(this.selectedAddress).then(async () => {
                        this._updateCurrentOperation(AddressOperation.Show, this.selectedAddress);
                        this._updateModuleState();
                        // tslint:disable-next-line:no-floating-promises
                        await this.updateCartDeliverySpecification();
                    });
                }
                break;
            default:
                // tslint:disable-next-line:no-floating-promises
                void this.updateCartDeliverySpecification();
                this.props.telemetry.error('Invalid operation');
        }
    };

    private onCancel = (): void => {
        switch (this.currentOperation) {
            case AddressOperation.Add:
            case AddressOperation.Update:
                this._resetView();
                break;
            case AddressOperation.Show:
                // @ts-ignore: Compiler not reconizing property form extending class
                this._resetView();
                break;
            default:
                this.props.telemetry.error('Invalid operation');
        }
    };

    private onEdit = (): void => {
        if (this.isBillingAddressSameAsShipping && this.shippingAddress && this.isCartContainsItemsForShipping) {
            this._updateCurrentOperation(AddressOperation.Show, this.shippingAddress);
        } else {
            this._updateCurrentOperation(AddressOperation.Add, this.billingAddress);
        }
        this.props.moduleState.onUpdating();
    };

    private _setBillingAddress = async (address: Address): Promise<void> => {
        if (this.props.data.checkout.result) {
            // tslint:disable-next-line: no-floating-promises
            await this.props.data.checkout.result.updateBillingAddress({ newBillingAddress: address }).then(async () => {
                const wineState = this.props.moduleState.getModuleByTypeName(
                  'checkout-club-info-collector'
                ).hasModuleState;
                const clubStore = WineClubDataStore.getInstance().get();
                if (wineState && clubStore.deliveryType === 'PICKUP') {
                  const checkoutCart = this.props.data.checkout.result
                    ?.checkoutCart;
                  const attributeValues =
                    (checkoutCart?.cart.AttributeValues as AttributeValue[]) ||
                    [];
                  const billingIndex = attributeValues.findIndex(
                    (item) => item.Name === 'BillingAddress'
                  );
                  const billingAddress: Address = {
                    AttentionTo: address.AttentionTo,
                    BuildingCompliment: address.BuildingCompliment,
                    City: address.City,
                    County: address.County,
                    DistrictName: address.DistrictName,
                    Email: address.Email,
                    IsAsyncAddress: address.IsAsyncAddress,
                    Name: address.Name,
                    Phone: address.Phone,
                    State: address.State,
                    Street: address.Street,
                    StreetNumber: address.StreetNumber,
                    ThreeLetterISORegionName: address.ThreeLetterISORegionName,
                    ZipCode: address.ZipCode,
                  };
                  const attr = {
                     // @ts-ignore
                      '@odata.type':
                        '#Microsoft.Dynamics.Commerce.Runtime.DataModel.AttributeTextValue',
                      Name: 'BillingAddress',
                      TextValue: JSON.stringify({ ...billingAddress }),
                      TextValueTranslations: []
                  };

                  if (billingIndex >= 0) {
                    attributeValues[billingIndex] = attr;
                  } else {
                    attributeValues.push(attr);
                  }
                  await updateAsync(
                    { callerContext: this.props.context.actionContext },
                    checkoutCart!.cart
                  );
                  await checkoutCart!.refreshCart({});
                }
            });
        }
    };

    private _resetView = () => {
        this._updateCurrentOperation(AddressOperation.Show, this.billingAddress);
        this._updateModuleState();
    };

    private _updateCurrentOperation = (operation: AddressOperation, selectedAddress?: Address) => {
        this.currentOperation = operation;
        this.selectedAddress = selectedAddress;

        if (this.currentOperation === AddressOperation.Add || this.currentOperation === AddressOperation.Update) {
            this.addUpdateAddress = { ...this.selectedAddress } || {};
            set(this.addUpdateAddress, { ['ThreeLetterISORegionName']: this.addUpdateAddress.ThreeLetterISORegionName || this.countryRegionId });
        }
    };

    private _updateModuleState = () => {
        if (this.currentOperation === AddressOperation.Show) {
            this.props.moduleState.onReady();
        } else {
            this.props.moduleState.onUpdating();
        }
    };
}

export default CheckoutBillingAddress;
